/**
 * 언어 목록 — **자동 생성 파일. 직접 고치지 마세요.**
 *
 * 정본은 `server/languages.py` 이고, 이 파일은 `scripts/gen_languages.py` 가
 * 옮겨 적은 결과다. 손으로 고치면 다음 생성 때 덮어써지고, 그 사이 서버와
 * 화면의 목록이 갈라진다(그 갈라짐이 이 파일이 생긴 이유다).
 *
 * 목록을 바꾸려면:
 *   1) `server/languages.py` 를 고치고
 *   2) `python scripts/gen_languages.py` 를 돌린다.
 */

/** 실측 등급 — **가용성 필터가 아니라 표시**다. 전 언어가 선택 가능하다. */
export type LanguageTier = 'verified' | 'supported' | 'limited'

export interface Language {
  code: string
  /** 한국어 표기 — UI 정본 */
  ko: string
  en: string
  tier: LanguageTier
  /** 원언어로 노출 */
  source: boolean
  /** 타깃 언어로 노출 */
  target: boolean
  /** 문자체계 부류(server Language.script 미러) — 양방향 자격 판정용 */
  script: string
  /** verified 가 아닌 언어의 사유 — UI 가 선택 후 설명으로 보여 준다 */
  note: string
}

/** 배지 문구 — tier 를 사람이 읽는 말로. */
export const TIER_LABEL: Record<LanguageTier, string> = {
  'verified': '검증됨',
  'supported': '지원',
  'limited': '제한적',
} as const

/**
 * 배지 설명(툴팁 기본값) — 사용자가 배지 뜻을 물어보지 않아도 알게 한다.
 *
 * `supported` 는 **강등이 아니다.** 고를 수 있고 잘 동작할 수도 있는 언어이며,
 * 다만 우리가 현행 통역 방식으로 아직 재 보지 않았을 뿐이다. 문구가 "안 된다"
 * 로 읽히면 사용자가 멀쩡히 쓸 수 있는 언어를 피하게 된다.
 */
export const TIER_HINT: Record<LanguageTier, string> = {
  'verified': '실제 회의 음성으로 전사·번역을 측정해 확인한 언어입니다.',
  'supported': '모델이 공식 지원하는 언어입니다. 선택해서 쓸 수 있으며, 현재 통역 방식으로는 아직 실측하지 않았습니다.',
  'limited': '공식 지원 언어이지만 이 배포에서 문제가 관측되었습니다.',
}

export const LANGUAGES: readonly Language[] = [
  { code: 'vi', ko: '베트남어', en: 'Vietnamese', tier: 'verified',
    source: true, target: true, script: 'latin', note: '' },
  { code: 'en', ko: '영어', en: 'English', tier: 'verified',
    source: true, target: false, script: 'latin', note: '' },
  { code: 'zh', ko: '중국어(표준)', en: 'Chinese (Mandarin)', tier: 'supported',
    source: true, target: false, script: 'han', note: '한국어 쪽은 실제 회의 음성으로 확인했지만, 중국어 발화 자체는 합성 음성으로만 시험했습니다. 실제 회의에서는 더 어려울 수 있습니다.' },
  { code: 'ja', ko: '일본어', en: 'Japanese', tier: 'supported',
    source: true, target: false, script: 'kana', note: '한국어 쪽은 실제 회의 음성으로 확인했지만, 일본어 발화 자체는 합성 음성으로만 시험했습니다. 실제 회의에서는 더 어려울 수 있습니다.' },
  { code: 'ko', ko: '한국어', en: 'Korean', tier: 'verified',
    source: true, target: true, script: 'hangul', note: '' },
  { code: 'nl', ko: '네덜란드어', en: 'Dutch', tier: 'supported',
    source: true, target: false, script: 'latin', note: '모델이 공식 지원하는 언어입니다. 다만 현재 통역 방식으로는 아직 실측하지 못했습니다 — 고를 수 있고 잘 동작할 수도 있습니다. 중요한 회의라면 먼저 짧게 시험해 보세요.' },
  { code: 'de', ko: '독일어', en: 'German', tier: 'supported',
    source: true, target: false, script: 'latin', note: '모델이 공식 지원하는 언어입니다. 다만 현재 통역 방식으로는 아직 실측하지 못했습니다 — 고를 수 있고 잘 동작할 수도 있습니다. 중요한 회의라면 먼저 짧게 시험해 보세요.' },
  { code: 'ru', ko: '러시아어', en: 'Russian', tier: 'supported',
    source: true, target: false, script: 'cyrillic', note: '모델이 공식 지원하는 언어입니다. 다만 현재 통역 방식으로는 아직 실측하지 못했습니다 — 고를 수 있고 잘 동작할 수도 있습니다. 중요한 회의라면 먼저 짧게 시험해 보세요.' },
  { code: 'ms', ko: '말레이어', en: 'Malay', tier: 'supported',
    source: true, target: false, script: 'latin', note: '모델이 공식 지원하는 언어입니다. 다만 현재 통역 방식으로는 아직 실측하지 못했습니다 — 고를 수 있고 잘 동작할 수도 있습니다. 중요한 회의라면 먼저 짧게 시험해 보세요.' },
  { code: 'ar', ko: '아랍어', en: 'Arabic', tier: 'supported',
    source: true, target: false, script: 'arabic', note: '모델이 공식 지원하는 언어입니다. 다만 현재 통역 방식으로는 아직 실측하지 못했습니다 — 고를 수 있고 잘 동작할 수도 있습니다. 중요한 회의라면 먼저 짧게 시험해 보세요.' },
  { code: 'ur', ko: '우르두어', en: 'Urdu', tier: 'limited',
    source: true, target: false, script: 'arabic', note: 'omni 가 우르두를 데바나가리로 전사 14/22 (DEF-116)' },
  { code: 'es', ko: '스페인어', en: 'Spanish', tier: 'supported',
    source: true, target: false, script: 'latin', note: '모델이 공식 지원하는 언어입니다. 다만 현재 통역 방식으로는 아직 실측하지 못했습니다 — 고를 수 있고 잘 동작할 수도 있습니다. 중요한 회의라면 먼저 짧게 시험해 보세요.' },
  { code: 'id', ko: '인도네시아어', en: 'Indonesian', tier: 'supported',
    source: true, target: false, script: 'latin', note: '한국어 쪽은 실제 회의 음성으로 확인했지만, 인도네시아어 발화 자체는 합성 음성으로만 시험했습니다. 실제 회의에서는 더 어려울 수 있습니다.' },
  { code: 'it', ko: '이탈리아어', en: 'Italian', tier: 'supported',
    source: true, target: false, script: 'latin', note: '모델이 공식 지원하는 언어입니다. 다만 현재 통역 방식으로는 아직 실측하지 못했습니다 — 고를 수 있고 잘 동작할 수도 있습니다. 중요한 회의라면 먼저 짧게 시험해 보세요.' },
  { code: 'th', ko: '태국어', en: 'Thai', tier: 'limited',
    source: true, target: false, script: 'thai', note: '태국어 방에서 한국어 발화가 태국어로 잘못 전사되는 현상이 관측됐습니다. 현재 설정은 이를 피하도록 맞춰 두었지만, 실제 태국어 음성으로는 아직 검증하지 못했습니다. 중요한 회의라면 먼저 짧게 시험해 보세요.' },
  { code: 'tr', ko: '튀르키예어', en: 'Turkish', tier: 'supported',
    source: true, target: false, script: 'latin', note: '모델이 공식 지원하는 언어입니다. 다만 현재 통역 방식으로는 아직 실측하지 못했습니다 — 고를 수 있고 잘 동작할 수도 있습니다. 중요한 회의라면 먼저 짧게 시험해 보세요.' },
  { code: 'pt', ko: '포르투갈어', en: 'Portuguese', tier: 'supported',
    source: true, target: false, script: 'latin', note: '모델이 공식 지원하는 언어입니다. 다만 현재 통역 방식으로는 아직 실측하지 못했습니다 — 고를 수 있고 잘 동작할 수도 있습니다. 중요한 회의라면 먼저 짧게 시험해 보세요.' },
  { code: 'fr', ko: '프랑스어', en: 'French', tier: 'supported',
    source: true, target: false, script: 'latin', note: '모델이 공식 지원하는 언어입니다. 다만 현재 통역 방식으로는 아직 실측하지 못했습니다 — 고를 수 있고 잘 동작할 수도 있습니다. 중요한 회의라면 먼저 짧게 시험해 보세요.' },
  { code: 'yue', ko: '광둥어', en: 'Cantonese', tier: 'supported',
    source: true, target: false, script: 'han', note: '모델이 공식 지원하는 언어입니다. 다만 현재 통역 방식으로는 아직 실측하지 못했습니다 — 고를 수 있고 잘 동작할 수도 있습니다. 중요한 회의라면 먼저 짧게 시험해 보세요.' },
]

/** 원언어 선택지 — 표시 순서 그대로. */
export const SOURCE_LANGUAGES: readonly Language[] =
  LANGUAGES.filter((l) => l.source)

/** 타깃 언어 선택지. */
export const TARGET_LANGUAGES: readonly Language[] =
  LANGUAGES.filter((l) => l.target)

/**
 * 개설 화면·호스트 콘솔이 실제로 내보이는 타깃 언어 집합 — 2026-09-22
 * 사용자 결정: "한국인 대상 통역이 기본 컨셉"이라 대상은 ko 하나뿐이다.
 * **UI 선택지 구성 전용 제품 상수**다 — 언어 판정·규칙 로직에는 절대
 * 쓰지 않는다. "언어 쌍을 고정하지 마라" 규칙(CLAUDE.md 4-0)의 예외가
 * 아니다: 그 규칙은 src/dst 판정 축이고, 이건 제품이 파는 타깃 언어 축이다.
 */
export const LANDING_TARGETS = ['ko'] as const

const BY_CODE = new Map(LANGUAGES.map((l) => [l.code, l]))

/** 한국어 표기 — 모르는 코드는 코드 그대로(표시가 비지 않게). */
export function displayKo(code: string): string {
  return BY_CODE.get(code)?.ko ?? code
}

export function getLanguage(code: string): Language | undefined {
  return BY_CODE.get(code)
}

/**
 * 역번역 **목적지**로 열린 코드 — `server/languages.py` 의
 * `REVERSE_TARGET_CODES` 미러. `target` 플래그와는 별개 축이다.
 */
export const REVERSE_TARGET_CODES: readonly string[] = ['ko', 'vi', 'fr']

/**
 * 양방향(§ bidi) 방으로 열 수 있는 언어 쌍인가 — **비대칭**이다
 * (bidi_asym D1(b)). `server/languages.py` 의 `is_bidirectional_pair`
 * 미러. dst 는 유효 타깃이어야 하고, src 는 원어이면서 역번역
 * 목적지로 열린 코드(REVERSE_TARGET_CODES)여야 한다.
 * src === dst 면 양방향은 의미가 없다.
 *
 * ⚠ 이 함수는 **생성물의 일부**다. 손으로 파일 끝에 붙여 두면
 *   다음 `gen_languages.py` 실행이 통째로 덮어써 조용히 사라진다
 *   (실제로 그럴 뻔했다) — 규칙이 바뀌면 이 생성기를 고쳐라.
 */
export function isBidirectionalPair(src: string, dst: string): boolean {
  const s = (src || '').toLowerCase()
  const d = (dst || '').toLowerCase()
  if (!s || !d || s === d) return false
  const sLang = BY_CODE.get(s)
  const dLang = BY_CODE.get(d)
  if (!sLang?.source || !dLang?.target) return false
  // 같은 문자체계 쌍은 라우팅이 두 언어를 못 가른다(server Language.script, 2차 교차리뷰)
  if (sLang.script === dLang.script) return false
  return REVERSE_TARGET_CODES.includes(s)
}
